import React, { useMemo, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import type { NotebookCell } from './NotebookEditor';
import { containsProxyContent } from '../../utils/proxyUrls';

interface VisualOutput {
  cellId: string;
  cellIndex: number;
  executionCount: number | null | undefined;
  type: 'image' | 'svg' | 'plotly' | 'proxy';
  data: any;
  mimeType: string;
  /** Extracted port number for proxy apps */
  port?: string;
  /** Title for plotly charts */
  title?: string;
}

interface ResultsPanelProps {
  cells: NotebookCell[];
  sessionId: string | null;
  proxyAuthCode: string | null;
  getProxyAuthCode: () => Promise<string | null>;
  onNavigateToCell: (cellId: string) => void;
}

export const ResultsPanel: React.FC<ResultsPanelProps> = ({
  cells,
  sessionId,
  proxyAuthCode,
  getProxyAuthCode,
  onNavigateToCell,
}) => {
  const [openingPort, setOpeningPort] = useState<string | null>(null);

  const visualOutputs = useMemo<VisualOutput[]>(() => {
    const results: VisualOutput[] = [];

    cells.forEach((cell, cellIndex) => {
      if (!cell.outputs) return;

      for (const output of cell.outputs) {
        if (output.type !== 'execute_result' && output.type !== 'display_data') continue;
        const data = output.data;
        if (!data) continue;

        // Plotly
        if (data['application/vnd.plotly.v1+json']) {
          const plotlyData = data['application/vnd.plotly.v1+json'];
          const rawTitle = plotlyData?.layout?.title?.text || plotlyData?.layout?.title || '';
          // Strip HTML tags from Plotly titles (they often contain <br><span> markup)
          const cleanTitle = typeof rawTitle === 'string'
            ? rawTitle.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            : '';
          results.push({
            cellId: cell.id,
            cellIndex,
            executionCount: cell.execution_count,
            type: 'plotly',
            data: plotlyData,
            mimeType: 'application/vnd.plotly.v1+json',
            title: cleanTitle || undefined,
          });
          continue;
        }

        // HTML with proxy content (Streamlit/Bokeh)
        if (data['text/html'] && typeof data['text/html'] === 'string') {
          const html = data['text/html'];
          if (containsProxyContent(html)) {
            const portMatch = html.match(/\/proxy\/(\d+)\//);
            results.push({
              cellId: cell.id,
              cellIndex,
              executionCount: cell.execution_count,
              type: 'proxy',
              data: html,
              mimeType: 'text/html',
              port: portMatch?.[1],
            });
            continue;
          }
        }

        // SVG
        if (data['image/svg+xml']) {
          results.push({
            cellId: cell.id,
            cellIndex,
            executionCount: cell.execution_count,
            type: 'svg',
            data: data['image/svg+xml'],
            mimeType: 'image/svg+xml',
          });
          continue;
        }

        // PNG / JPEG
        for (const mime of ['image/png', 'image/jpeg'] as const) {
          if (data[mime]) {
            results.push({
              cellId: cell.id,
              cellIndex,
              executionCount: cell.execution_count,
              type: 'image',
              data: data[mime],
              mimeType: mime,
            });
            break;
          }
        }
      }
    });

    return results;
  }, [cells]);

  const handleOpenProxyWindow = useCallback(async (port: string) => {
    if (!sessionId) return;
    setOpeningPort(port);
    try {
      // StandaloneProxyPage fetches its own fresh auth code on mount,
      // so we only need to pass sessionId and port.
      const params = new URLSearchParams({ sessionId, port });
      window.open(
        `/proxy-window?${params.toString()}`,
        `proxy-${sessionId}-${port}`,
        'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no',
      );
    } finally {
      setOpeningPort(null);
    }
  }, [sessionId]);

  if (visualOutputs.length === 0) {
    return (
      <div className="p-4 text-center">
        <div className="text-gray-600 mb-2">
          <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-xs text-gray-500">
          No visual outputs yet. Run cells that produce charts, images, or apps.
        </p>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-2">
      <div className="flex items-center justify-between px-1 py-1">
        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
          Outputs
          <span className="ml-1.5 text-gray-600">({visualOutputs.length})</span>
        </span>
      </div>

      {visualOutputs.map((item, idx) => (
        <div
          key={`${item.cellId}-${idx}`}
          className="group relative bg-gray-900/60 border border-gray-800/40 rounded overflow-hidden hover:border-gray-700/60 transition-colors cursor-pointer"
          onClick={() => {
            onNavigateToCell(item.cellId);
            document.getElementById(`cell-${item.cellId}`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }}
        >
          {/* Execution count badge */}
          {item.executionCount != null && (
            <div className="absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 bg-black/70 rounded text-[9px] text-gray-400 font-mono">
              In [{item.executionCount}]
            </div>
          )}

          {/* Content */}
          {item.type === 'image' && (
            <div className="p-2 flex items-center justify-center bg-gray-950/50">
              <img
                src={`data:${item.mimeType};base64,${item.data}`}
                alt={`Output from cell ${item.cellIndex + 1}`}
                className="max-h-20 max-w-full object-contain rounded"
              />
            </div>
          )}

          {item.type === 'svg' && (
            <div
              className="p-2 flex items-center justify-center bg-gray-950/50 max-h-20 overflow-hidden [&>svg]:max-h-16 [&>svg]:max-w-full"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.data, { USE_PROFILES: { svg: true } }),
              }}
            />
          )}

          {item.type === 'plotly' && (
            <div className="p-3 flex items-center gap-3 bg-gray-950/50">
              <div className="w-10 h-10 rounded bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="text-xs text-white truncate">
                  {item.title || 'Plotly Chart'}
                </div>
                <div className="text-[10px] text-gray-500">Interactive chart</div>
              </div>
            </div>
          )}

          {item.type === 'proxy' && (
            <div className="p-3 flex items-center gap-3 bg-gray-950/50">
              <div className="w-10 h-10 rounded bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white">
                  App{item.port ? ` :${item.port}` : ''}
                </div>
                <div className="text-[10px] text-gray-500">Embedded app</div>
              </div>
              {item.port && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenProxyWindow(item.port!);
                  }}
                  disabled={openingPort === item.port}
                  className="shrink-0 px-2.5 py-1.5 text-[10px] font-medium text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded hover:bg-purple-500/20 transition-colors disabled:opacity-50"
                >
                  {openingPort === item.port ? 'Opening...' : 'Open in Window'}
                </button>
              )}
            </div>
          )}

          {/* Subtle cell index at bottom-right */}
          <div className="absolute bottom-1 right-1.5 text-[9px] text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
            cell {item.cellIndex + 1}
          </div>
        </div>
      ))}
    </div>
  );
};
